import { MessagesThread } from '@/customer/components/MessagesThread';
import { useEffect, useState } from 'react';

/**
 * Staff-side wrapper around the shared MessagesThread component. Renders the
 * thread for a given booking with the staff context, plus an unread badge
 * surfaced from the backend.
 *
 * The component is intentionally narrow — it owns the unread badge fetch +
 * the moderate-capability flag, but defers all rendering + send + mark-read
 * to the shared customer-side MessagesThread. The realtime subscription is
 * piped in via `realtimeNonce` from the parent (today / booking detail / etc.).
 */

export interface BookingMessagesPanelProps {
  bookingId: string;
  /** True if the current staff user has the `messages.moderate` capability. */
  canModerate?: boolean | undefined;
  /** Bumps on BOOKING_MESSAGE_POSTED events for this bookingId. */
  realtimeNonce?: number | undefined;
}

export function BookingMessagesPanel(props: BookingMessagesPanelProps) {
  return (
    <div data-testid="booking-messages-panel">
      <MessagesThread
        bookingId={props.bookingId}
        context="staff"
        canModerate={props.canModerate}
        realtimeNonce={props.realtimeNonce}
      />
    </div>
  );
}

/**
 * Small button surface that fetches + renders the unread customer-message
 * count for a booking. Used as a badge on the staff sidebar / today list.
 *
 * Reads from `GET /api/bookings/:id/messages` and counts unread customer
 * messages client-side. A dedicated count endpoint can replace this when
 * the spec adds an /unread-count endpoint; for now the same list call is
 * cheap.
 */
export interface UnreadBadgeProps {
  bookingId: string;
  realtimeNonce?: number | undefined;
}

interface ApiMessage {
  id: string;
  sender: 'customer' | 'staff';
  readAt: string | null;
  deletedAt: string | null;
}

export function UnreadBadge({ bookingId, realtimeNonce }: UnreadBadgeProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/bookings/${encodeURIComponent(bookingId)}/messages?limit=100`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return { messages: [] as ApiMessage[] };
        return (await res.json()) as { messages: ApiMessage[] };
      })
      .then((body) => {
        if (cancelled) return;
        const unread = (body.messages ?? []).filter(
          (m) => m.sender === 'customer' && !m.readAt && !m.deletedAt,
        ).length;
        setCount(unread);
      })
      .catch(() => {
        if (!cancelled) setCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, realtimeNonce]);
  if (count === 0) return null;
  return (
    <span
      data-testid={`unread-badge-${bookingId}`}
      className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-white"
    >
      {count}
    </span>
  );
}
